import { FC, useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAppSelector } from '../../../redux/hooks';
import { useOnFarmActionBlocked } from '../../../hooks/useOnFarmActionBlocked';
import { BackButton, Modal, SelectStorageItems } from '../../../components';
import ActionMenu from '../../../components/action-buttons/ActionButtons';
import PagedTable from '../../../components/sorted-table/PagedTable';
import SpecimenLotInfo from '../../../components/specimen-lot-info/SpecimenLotInfo';
import { MoveManagementBaseAutoFill } from './OnFarmMove';
import OnFarmSpecimenViewEdit from '../animal-management/OnFarmSpecimenViewEdit';
import { StorageTankOptions } from '../../../types/enums';
import { selectOption } from '../../../types/interfaces';
import { OnFarmSpecimenLocation } from '../../../types/interfaces/onFarmSpecimen.interfaces';
import { getAccountStorageCanisterCount, getAccountStorageTankInventoryCount } from '../../../api/accountStorageItemsApi';
import { getOnFarmSpecimenLocations } from '../../../api/onFarmSpecimenLocationApi';
import { showToast } from '../../../services/toast.service';
import { BUTTON_CONSTANTS, LABEL_CONSTANTS, PAGE_HEADER_CONSTANTS, TABLE_HEADER_CONSTANTS } from '../../../constants/common';
import { toastMessages } from '../../../constants/errorMessages';
import { ROUTE_PATHS } from '../../../constants/routePaths';

const OnFarmCanisterContents: FC = (): JSX.Element => {
  const navigate = useNavigate();
  const { state: autoFill } = useLocation();
  const { accountId } = useAppSelector(state => state.activeAccount);
  const isInventoryLocked = useOnFarmActionBlocked();
  const [site, setSite] = useState<selectOption>(autoFill?.site ?? { value: '', name: '' });
  const [tank, setTank] = useState<selectOption>(autoFill?.tank ?? { value: '', name: '' });
  const [canister, setCanister] = useState<selectOption>(autoFill?.canister ?? { value: '', name: '' });
  const [canisterCount, setCanisterCount] = useState<number>();
  const [tankInventoryCount, setTankInventoryCount] = useState<number>();
  const [canisterContents, setCanisterContents] = useState<OnFarmSpecimenLocation[]>();
  const [specimenInfoModal, setSpecimenInfoModal] = useState<JSX.Element>();
  const [refresh, setRefresh] = useState<number>(0);

  const hasCanisterSelected = canister.value.length > 0;

  useEffect(() => {
    const getCanisterCount = async () => {
      try {
        const { data: count } = await getAccountStorageCanisterCount(canister.value);
        setCanisterCount(count);
      } catch {
        setCanisterCount(undefined);
        showToast.error(toastMessages.SOMETHING_WENT_WRONG);
      }
    };
    if (hasCanisterSelected) {
      getCanisterCount();
    } else {
      setCanisterCount(undefined);
    }
  }, [canister, hasCanisterSelected]);

  useEffect(() => {
    const getTankInventoryCount = async () => {
      try {
        const { data: count } = await getAccountStorageTankInventoryCount(tank.value);
        setTankInventoryCount(count);
      } catch {
        setTankInventoryCount(undefined);
        showToast.error(toastMessages.SOMETHING_WENT_WRONG);
      }
    };

    if (tank.value.length > 0) {
      getTankInventoryCount();
    } else {
      setTankInventoryCount(undefined);
    }
  }, [tank]);

  const handleGetLocations = useCallback(async () => {
    if (!accountId || canister.value.length === 0) {
      setCanisterContents(undefined);
      return;
    }

    try {
      const { data: specimenLocations } = await getOnFarmSpecimenLocations({
        include: 'OnFarmSpecimen.OnFarmAnimal',
        filter: `AccountStorageCanisterId eq ${canister.value}`,
      });
      setCanisterContents(specimenLocations);
    } catch {
      showToast.error(toastMessages.SOMETHING_WENT_WRONG);
    }
  }, [accountId, canister]);

  useEffect(() => {
    handleGetLocations();
  }, [handleGetLocations]);

  const moveSpecimen = (item: OnFarmSpecimenLocation) => {
    const inventoryMoveAutoFillState: MoveManagementBaseAutoFill = {
      site: site,
      tank: tank,
      canister: canister,
      selectedSpecimenId: item.onFarmSpecimenId,
    };

    navigate(ROUTE_PATHS.APP_ON_FARM_MANAGEMENT_MOVE, { state: inventoryMoveAutoFillState });
  };

  const lotInfoSpecimen = (item: OnFarmSpecimenLocation) => {
    setSpecimenInfoModal(<OnFarmSpecimenViewEdit onFarmSpecimenId={item.onFarmSpecimenId} />);
  };

  const contentActions = (item: OnFarmSpecimenLocation) => [
    ...(!isInventoryLocked ? [{ name: BUTTON_CONSTANTS.MOVE, action: () => moveSpecimen(item) }] : []),
    { name: BUTTON_CONSTANTS.LOT_INFO, action: () => lotInfoSpecimen(item) },
  ];

  const locationParts = [site.name, tank.name, canister.name].filter(Boolean);

  return (
    <div className="on-farm on-farm-management-canister-contents">
      <div className="header">
        <div className="header-title-block">
          <h1>{PAGE_HEADER_CONSTANTS.ON_FARM_CANISTER_CONTENTS}</h1>
          {locationParts.length > 0 && (
            <p className="location-breadcrumb" aria-label="Selected location">
              {locationParts.join(' · ')}
            </p>
          )}
        </div>
        <SelectStorageItems
          accountId={accountId}
          selectedSite={site}
          selectedTank={tank}
          selectedCanister={canister}
          setSelectedSite={setSite}
          setSelectedTank={setTank}
          setSelectedCanister={setCanister}
          storageTankOptions={StorageTankOptions.Both}
          activeLocationsOnly
        />
      </div>

      <Modal isOpen={specimenInfoModal !== undefined} onClose={() => setSpecimenInfoModal(undefined)} width={700}>
        <div className="specimen-info-modal">
          <BackButton onClick={() => setSpecimenInfoModal(undefined)} />
          {specimenInfoModal}
        </div>
      </Modal>

      <div className="inventory-counts">
        <div className="inventory-stat inventory-stat--tank">
          <span className="inventory-stat__label">{LABEL_CONSTANTS.TANK_INVENTORY_COUNT}</span>
          <strong className="inventory-stat__value">{tankInventoryCount ?? '--'}</strong>
        </div>
        <div className="inventory-stat inventory-stat--canister">
          <span className="inventory-stat__label">{LABEL_CONSTANTS.CANISTER_INVENTORY_COUNT}</span>
          <strong className="inventory-stat__value">{canisterCount ?? '--'}</strong>
        </div>
      </div>

      <div className="content">
        {hasCanisterSelected && accountId ? (
          <PagedTable
            headers={[
              { displayName: TABLE_HEADER_CONSTANTS.ANIMAL_CODE_NAME },
              { displayName: TABLE_HEADER_CONSTANTS.LOT_DATE_NO },
              { displayName: TABLE_HEADER_CONSTANTS.QUANTITY },
              { displayName: TABLE_HEADER_CONSTANTS.ACTIONS },
            ]}
            buildRow={(item: OnFarmSpecimenLocation) => {
              return [
                `${item.onFarmSpecimen?.onFarmAnimal?.code ?? ''} - ${item.onFarmSpecimen?.onFarmAnimal?.name ?? ''}`,
                <SpecimenLotInfo
                  specimenId={item.onFarmSpecimenId}
                  lotNumber={item.onFarmSpecimen?.lotNumber}
                  freezeDate={item.onFarmSpecimen?.freezeDate}
                />,
                item.quantity,
                <ActionMenu actionButtons={contentActions(item)} />,
              ];
            }}
            getData={getOnFarmSpecimenLocations}
            filter={`AccountStorageCanisterId eq ${canister.value}`}
            height={500}
            minHeight={220}
            include="OnFarmSpecimen.OnFarmAnimal"
            pageSize={12}
            scrollBufferThickness={50}
            sortBy=""
            refresh={refresh}
            setRefresh={setRefresh}
          />
        ) : (
          <div className="empty-canister-state">
            <p>Select a site, tank, and canister to view inventory contents.</p>
          </div>
        )}
      </div>

      <div className="xs-content">
        {hasCanisterSelected && accountId ? (
          canisterContents && canisterContents.length > 0 ? (
            canisterContents.map((canisterContent, index) => {
              const animalCode = canisterContent.onFarmSpecimen?.onFarmAnimal?.code ?? '';
              const animalName = canisterContent.onFarmSpecimen?.onFarmAnimal?.name ?? '';

              return (
                <div className="card max-width canister-card" key={`${canisterContent.onFarmSpecimenId}-${index}`}>
                  <div className="canister-inventory">
                    <div className="canister-inventory-content">
                      <div className="animal-code">
                        <span className="field-label">{TABLE_HEADER_CONSTANTS.ANIMAL_CODE_NAME}</span>
                        <span className="field-value">{`${animalCode} - ${animalName}`}</span>
                      </div>
                      <div className="animal-info">
                        <div className="animal-lot-date">
                          <span className="field-label">{TABLE_HEADER_CONSTANTS.LOT_DATE_NO}</span>
                          <SpecimenLotInfo
                            specimenId={canisterContent.onFarmSpecimenId}
                            lotNumber={canisterContent.onFarmSpecimen?.lotNumber}
                            freezeDate={canisterContent.onFarmSpecimen?.freezeDate}
                          />
                        </div>
                        <div className="quantity">
                          <span className="field-label">{TABLE_HEADER_CONSTANTS.QUANTITY}</span>
                          <span className="field-value quantity-value">{canisterContent.quantity}</span>
                        </div>
                      </div>
                    </div>
                    <div className="action-button">
                      <ActionMenu actionButtons={contentActions(canisterContent)} />
                    </div>
                  </div>
                </div>
              );
            })
          ) : (
            <div className="empty-canister-state">
              <p>No specimens found in this canister.</p>
            </div>
          )
        ) : (
          <div className="empty-canister-state">
            <p>Select a site, tank, and canister to view inventory contents.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default OnFarmCanisterContents;
